import React, { useState } from "react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Menu,
  Stack,
  CircularProgress,
  Divider,
} from "@mui/material";
import { 
  DeleteOutline, 
  ExpandMore, 
  CalendarToday, 
  Person,
} from "@mui/icons-material";
import api from "state/axios";
import { 
  REQUEST_TYPE_OPTIONS, 
  STATUS_OPTIONS, 
  STATUS_COLOR, 
} from "./constants";


function StatusMenu({ row, onUpdated }) {
  const [anchor, setAnchor]   = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = async (newStatus) => {
    setAnchor(null);
    if (newStatus === row.status) return;
    setLoading(true);
    try {
      await api.patch(`/datarequests/status/${row._id}`, { status: newStatus });
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update status failed!");
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Chip
        label={row.status}
        size="small"
        color={STATUS_COLOR[row.status] || "default"}
        onClick={(e) => setAnchor(e.currentTarget)}
        onDelete={(e) => setAnchor(e.currentTarget)}
        deleteIcon={loading ? <CircularProgress size={12} /> : <ExpandMore />}
        sx={{ 
          cursor: "pointer", 
          justifyContent: "space-between", 
          minWidth: 100, 
        }}
      />
      <Menu 
        anchorEl={anchor} 
        open={Boolean(anchor)} 
        onClose={() => setAnchor(null)}
      >
        {STATUS_OPTIONS?.map((s) => (
          <MenuItem 
            key={s} 
            selected={s === row.status} 
            onClick={() => handleChange(s)}
          >
            <Chip
              label={s}
              size="small"
              color={STATUS_COLOR[s] || "default"}
              sx={{ 
                pointerEvents: "none", 
                justifyContent: "space-between", 
                minWidth: 80, 
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}


function RequestCard({ row, onDelete, onStatusUpdated }) {
  const dateRange = `${format(new Date(row.startDate), "dd/MM/yyyy")} - ${format(new Date(row.endDate), "dd/MM/yyyy")}`;
  const createdBy = row.createdBy
    ? `${row.createdBy.firstName || ""} ${row.createdBy.lastName || ""}`.trim()
    : null;


  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: "12px !important" }}>
        <Stack 
          direction="row" 
          alignItems="center" 
          justifyContent="space-between" 
          mb={1}
        >
          <StatusMenu row={row} onUpdated={onStatusUpdated} />
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => onDelete([row._id])}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack 
          direction="row" 
          spacing={0.5} 
          alignItems="center" 
          mb={0.75}
        >
          <CalendarToday sx={{ fontSize: 13, color: "text.secondary" }} />
          <Typography variant="body2">{dateRange}</Typography>
          {row.crewCode && (
            <>
              <Typography variant="body2" color="text.disabled" mx={0.5}>·</Typography>
              <Person sx={{ fontSize: 13, color: "text.secondary" }} />
              <Typography variant="body2">{row.crewCode}</Typography>
            </>
          )}
        </Stack>

        <Divider sx={{ my: 0.75 }} />

        <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.75}>
          {row.requestTypes?.map((t) => {
            const label = REQUEST_TYPE_OPTIONS.find((r) => r.value === t)?.label || t;
            return (
              <Chip key={t} label={label} size="small" variant="outlined" sx={{ fontSize: "0.7rem", height: 22 }} />
            );
          })}
        </Box>

        {(createdBy || row.createdAt) && (
          <Typography variant="caption" color="text.disabled" display="block" mt={0.75}>
            {[createdBy, row.createdAt ? format(new Date(row.createdAt), "dd/MM HH:mm") : null]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}


export default function RequestCards({ rows, loading, onDelete, onStatusUpdated }) {
  const pendingCount    = rows.filter((r) => r.status === "Pending").length;
  const processingCount = rows.filter((r) => r.status === "Processing").length;
  const doneCount       = rows.filter((r) => r.status === "Done").length;
  const failedCount     = rows.filter((r) => r.status === "Failed").length;

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1} mb={1.5}>
        <Typography variant="h6" fontWeight={600}>Requests</Typography>
        <Chip size="small" label={`${pendingCount} Pending`}       color="warning" variant="outlined" />
        <Chip size="small" label={`${processingCount} Processing`} color="info"    variant="outlined" />
        <Chip size="small" label={`${doneCount} Done`}             color="success" variant="outlined" />
        {failedCount > 0 && (
          <Chip size="small" label={`${failedCount} Failed`} color="error" variant="outlined" />
        )}
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
          No requests found.
        </Typography>
      ) : (
        rows.map((row) => (
          <RequestCard
            key={row._id}
            row={row}
            onDelete={onDelete}
            onStatusUpdated={onStatusUpdated}
          />
        ))
      )}
    </Box>
  );
}
